import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { saveOrganizationSOP, getOrganizationSOP } from '../../Services/OrganizationService';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const validationSchema = Yup.object({
  evb: Yup.string(),
  medicalCoding: Yup.string(),
  medicalCodingAudit: Yup.string(),
  claimScrubbing: Yup.string(),
  claimTransfer: Yup.string(),
  clearingHouseRejectionMgmt: Yup.string(),
  paymentPosting: Yup.string(),
  coPayPosting: Yup.string(),
  paperCorrespondence: Yup.string(),
  denialManagement: Yup.string(),
  arFollowUp: Yup.string(),
  arFollowUpNotes: Yup.string(),
  patientStatement: Yup.string(),
  patientSupportTollNumber: Yup.string(),
  frequency: Yup.number().typeError('Must be a number'),
  coPayPostingNotes: Yup.string(),
});

const DEFAULT_OPTIONS = ['Aegis Healthcare Solutions', 'Client'];
const TAT_OPTIONS = ['0-12 hours - Aegis', '12-24 hours - Aegis', '24-48 hours - Aegis', 'Client'];
const DENIAL_OPTIONS = ['0-12 hours - Aegis', '12-24 hours - Aegis', '24-48 hours - Aegis', 'Client'];
const PATIENT_STATEMENT_OPTIONS = ['Once in a week', 'Once in every 15 days', 'Once in every 30 days'];

const normalizeDropdownValue = (value, clientCid) => {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (/^(aegis|aegis healthcare|aegis healthcare solutions)$/i.test(trimmed)) {
    return 'Aegis Healthcare Solutions';
  }
  const normalizedClient = trimmed.replace(/\s*[-–]\s*/, ' - ');
  if (/^client(?:\s*[-–]\s*.*)?$/i.test(trimmed)) {
    return normalizedClient === 'Client' ? (clientCid ? formatClientOptionLabel(clientCid) : 'Client') : normalizedClient;
  }
  return trimmed;
};

const formatClientOptionLabel = (clientCid) => (clientCid ? `Client - ${clientCid}` : 'Client');

export default function SOP({ onSaveSuccess, onContinue, organizationId, clientCid }) {
  const { token } = useAuth();
  const { id: orgIdFromParams } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [sopId, setSopId] = useState(null);

  const effectiveOrgId = organizationId || orgIdFromParams || null;

  const getOptionValue = (option) => (option === 'Client' ? formatClientOptionLabel(clientCid) : option);

  const handleSaveForm = async (values) => {
    setLoading(true);
    try {
      const payload = {
        id: sopId || null,
        organization_id: values.organizationId || effectiveOrgId || null,
        evb: values.evb || null,
        medical_coding: values.medicalCoding || null,
        medical_coding_audit: values.medicalCodingAudit || null,
        claim_scrubbing: values.claimScrubbing || null,
        claim_transfer: values.claimTransfer || null,
        clearing_house_rejection_mgmt: values.clearingHouseRejectionMgmt || null,
        payment_posting: values.paymentPosting || null,
        co_pay_posting: values.coPayPosting || null,
        paper_correspondence: values.paperCorrespondence || null,
        denial_management: values.denialManagement || null,
        ar_follow_up: values.arFollowUp || null,
        ar_follow_up_notes: values.arFollowUpNotes || null,
        patient_statement: values.patientStatement || null,
        patient_support_toll_number: values.patientSupportTollNumber || null,
        frequency: values.frequency || null,
        co_pay_posting_notes: values.coPayPostingNotes || null,
        type: sopId ? 'update' : 'create',
      };

      const res = await saveOrganizationSOP(payload, token);
      if (res?.is_success) {
        const returned = res.data ?? res;
        if (returned?.id) setSopId(returned.id);
        toast.success(res?.message || 'SOP saved');
        onSaveSuccess?.();
        onContinue?.();
      } else if (res?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        console.error('Failed to save SOP', res);
      }
      else {
        toast.error(res?.message || 'Failed to save SOP');
        console.error('Failed to save SOP', res);
      }
    } catch (error) {
      console.error('Error saving SOP:', error);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = React.useMemo(() => ({
    organizationId: effectiveOrgId,
    evb: '',
    medicalCoding: '',
    medicalCodingAudit: '',
    claimScrubbing: '',
    claimTransfer: '',
    clearingHouseRejectionMgmt: '',
    paymentPosting: '',
    coPayPosting: '',
    paperCorrespondence: '',
    denialManagement: '',
    arFollowUp: '',
    arFollowUpNotes: '',
    patientStatement: '',
    patientSupportTollNumber: '',
    frequency: '',
    coPayPostingNotes: '',
  }), [effectiveOrgId]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSaveForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Load SOP for organization if organizationId is provided via props in parent form
  useEffect(() => {
    const load = async () => {
      const orgId = effectiveOrgId || formik.values.organizationId || null;
      if (!orgId) return;
      setFetching(true);
      try {
        const res = await getOrganizationSOP(orgId, token);
        if (res?.is_success) {
          const d = res.data ?? res;
          setSopId(d.id ?? null);
          formik.setValues({
            organizationId: d.organization_id ?? orgId,
            evb: normalizeDropdownValue(d.evb ?? '', clientCid),
            medicalCoding: normalizeDropdownValue(d.medical_coding ?? '', clientCid),
            medicalCodingAudit: normalizeDropdownValue(d.medical_coding_audit ?? '', clientCid),
            claimScrubbing: normalizeDropdownValue(d.claim_scrubbing ?? '', clientCid),
            claimTransfer: d.claim_transfer ?? '',
            clearingHouseRejectionMgmt: d.clearing_house_rejection_mgmt ?? '',
            paymentPosting: d.payment_posting ?? '',
            coPayPosting: normalizeDropdownValue(d.co_pay_posting ?? '', clientCid),
            paperCorrespondence: d.paper_correspondence ?? '',
            denialManagement: d.denial_management ?? '',
            arFollowUp: normalizeDropdownValue(d.ar_follow_up ?? '', clientCid),
            arFollowUpNotes: d.ar_follow_up_notes ?? '',
            patientStatement: d.patient_statement ?? '',
            patientSupportTollNumber: d.patient_support_toll_number ?? '',
            frequency: d.frequency ?? '',
            coPayPostingNotes: d.co_pay_posting_notes ?? '',
          });
        }
      } catch (err) {
        console.error('Error loading SOP:', err);
      } finally {
        setFetching(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, token]);


  const getFieldError = (fieldName) => {
    const keys = fieldName.split('.');
    let error = formik.errors;
    for (const key of keys) {
      error = error?.[key];
    }
    return error;
  };

  const getFieldTouched = (fieldName) => {
    const keys = fieldName.split('.');
    let touched = formik.touched;
    for (const key of keys) {
      touched = touched?.[key];
    }
    return touched;
  };

  const renderFieldError = (fieldName) => {
    const error = getFieldError(fieldName);
    const touched = getFieldTouched(fieldName);
    return touched && error ? <div className="invalid-feedback d-block">{error}</div> : null;
  };

  const renderAsterisk = () => <span className="text-danger">*</span>;

  const formatPhoneInput = (input) => {
    const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePatientSupportTollChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    formik.setFieldValue('patientSupportTollNumber', formatted);
  };

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className="py-3 px-0 px-md-3">
        {fetching ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* General Settings Section */}
            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
              <h6 className="fw-bold hc-text-primary mb-3">Eligibility & Benefits Verfication Service</h6>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">EVB</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('evb') && getFieldError('evb') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('evb')}
                  >
                    <option value="">Select EVB</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('evb')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Frequency (in days)</label>
                  <input
                    type="number"
                    className={`form-control rounded-3 shadow-sm ${getFieldTouched('frequency') && getFieldError('frequency') ? 'is-invalid' : ''}`}
                    placeholder="Enter frequency value"
                    {...formik.getFieldProps('frequency')}
                  />
                  {renderFieldError('frequency')}
                </div>
              </div>
            </div>

            {/* Billing Services Section */}
            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
              <h6 className="fw-bold hc-text-primary mb-3">Billing Services</h6>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Medical Coding</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('medicalCoding') && getFieldError('medicalCoding') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('medicalCoding')}
                  >
                    <option value="">Select option</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('medicalCoding')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Medical Coding Audit</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('medicalCodingAudit') && getFieldError('medicalCodingAudit') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('medicalCodingAudit')}
                  >
                    <option value="">Select option</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('medicalCodingAudit')}
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Claim Scrubbing</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('claimScrubbing') && getFieldError('claimScrubbing') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('claimScrubbing')}
                  >
                    <option value="">Select option</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('claimScrubbing')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Claim Transfer TAT</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('claimTransfer') && getFieldError('claimTransfer') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('claimTransfer')}
                  >
                    <option value="">Select option</option>
                    {TAT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('claimTransfer')}
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Clearing House Rejection Management</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('clearingHouseRejectionMgmt') && getFieldError('clearingHouseRejectionMgmt') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('clearingHouseRejectionMgmt')}
                  >
                    <option value="">Select option</option>
                    {TAT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('clearingHouseRejectionMgmt')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Payment Posting</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('paymentPosting') && getFieldError('paymentPosting') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('paymentPosting')}
                  >
                    <option value="">Select option</option>
                    {TAT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('paymentPosting')}
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Co-Pay Posting</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('coPayPosting') && getFieldError('coPayPosting') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('coPayPosting')}
                  >
                    <option value="">Select option</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('coPayPosting')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Denial Management</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('denialManagement') && getFieldError('denialManagement') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('denialManagement')}
                  >
                    <option value="">Select option</option>
                    {DENIAL_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('denialManagement')}
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Co-Pay Posting Notes</label>
                  <textarea
                    className={`form-control rounded-3 shadow-sm ${getFieldTouched('coPayPostingNotes') && getFieldError('coPayPostingNotes') ? 'is-invalid' : ''}`}
                    placeholder="Enter additional notes"
                    rows="3"
                    {...formik.getFieldProps('coPayPostingNotes')}
                  />
                  {renderFieldError('coPayPostingNotes')}
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">Paper Correspondence</label>
                  <textarea
                    className={`form-control rounded-3 shadow-sm ${getFieldTouched('paperCorrespondence') && getFieldError('paperCorrespondence') ? 'is-invalid' : ''}`}
                    placeholder="Enter paper correspondence details"
                    rows="3"
                    {...formik.getFieldProps('paperCorrespondence')}
                  />
                  {renderFieldError('paperCorrespondence')}
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">AR Follow-up</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('arFollowUp') && getFieldError('arFollowUp') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('arFollowUp')}
                  >
                    <option value="">Select option</option>
                    {DEFAULT_OPTIONS.map((option) => {
                      const label = option === 'Client' ? formatClientOptionLabel(clientCid) : option;
                      return (
                        <option key={option} value={getOptionValue(option)}>{label}</option>
                      );
                    })}
                  </select>
                  {renderFieldError('arFollowUp')}
                </div>
              </div>

              <div className="row g-3 mt-2">
                <div className="col-md-12">
                  <label className="form-label fw-semibold">AR Follow-up Notes</label>
                  <textarea
                    className={`form-control rounded-3 shadow-sm ${getFieldTouched('arFollowUpNotes') && getFieldError('arFollowUpNotes') ? 'is-invalid' : ''}`}
                    placeholder="Enter AR follow-up notes"
                    rows="3"
                    {...formik.getFieldProps('arFollowUpNotes')}
                  />
                  {renderFieldError('arFollowUpNotes')}
                </div>
              </div>

              <div className="row g-3 my-2">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Patient Statements</label>
                  <select
                    className={`form-select rounded-3 shadow-sm ${getFieldTouched('patientStatement') && getFieldError('patientStatement') ? 'is-invalid' : ''}`}
                    {...formik.getFieldProps('patientStatement')}
                  >
                    <option value="">Select option</option>
                    {PATIENT_STATEMENT_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {renderFieldError('patientStatement')}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Patient Support Toll-free Number</label>
                  <input
                    type="text"
                    name="patientSupportTollNumber"
                    className={`form-control rounded-3 shadow-sm ${getFieldTouched('patientSupportTollNumber') && getFieldError('patientSupportTollNumber') ? 'is-invalid' : ''}`}
                    placeholder="123-123-1234"
                    value={formik.values.patientSupportTollNumber}
                    onChange={handlePatientSupportTollChange}
                    onBlur={formik.handleBlur}
                  />
                  {renderFieldError('patientSupportTollNumber')}
                </div>
              </div>
            </div>

            {/* Footer with Save & Continue Button */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="submit"
                className="btn hc-bg-primary text-white px-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save & Continue'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
}

